type SpeakerMuteRangeLike = {
    startSec: number;
    endSec: number;
    sourceIndex?: number;
};

type TimeRange = {
    startSec: number;
    endSec: number;
};

/**
 * Subtract speaker mute ranges from a timeline interval and return the audible pieces.
 * Mute ranges with undefined sourceIndex apply to all sources.
 */
export function splitTimelineRangeBySpeakerMutes(args: {
    startSec: number;
    endSec: number;
    sourceIndex: number;
    muteRanges?: SpeakerMuteRangeLike[] | null;
    eps?: number;
}): TimeRange[] {
    const EPS = Number(args.eps ?? 1e-3);
    const startSec = Number(args.startSec ?? NaN);
    const endSec = Number(args.endSec ?? NaN);
    const sourceIndex = Number(args.sourceIndex ?? NaN);
    if (!Number.isFinite(startSec) || !Number.isFinite(endSec) || endSec <= startSec + EPS) return [];
    if (!Number.isFinite(sourceIndex) || sourceIndex < 0) return [{ startSec, endSec }];

    const relevant: TimeRange[] = [];
    for (const r of args.muteRanges ?? []) {
        const s = Number((r as any)?.startSec ?? NaN);
        const e = Number((r as any)?.endSec ?? NaN);
        if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s + EPS) continue;

        const src = Number((r as any)?.sourceIndex);
        const appliesToSource = !Number.isFinite(src) || src === sourceIndex;
        if (!appliesToSource) continue;

        const overlapStart = Math.max(startSec, s);
        const overlapEnd = Math.min(endSec, e);
        if (overlapEnd <= overlapStart + EPS) continue;
        relevant.push({ startSec: overlapStart, endSec: overlapEnd });
    }

    if (relevant.length === 0) {
        return [{ startSec, endSec }];
    }

    relevant.sort((a, b) => a.startSec - b.startSec || a.endSec - b.endSec);

    const merged: TimeRange[] = [];
    for (const r of relevant) {
        if (!merged.length) {
            merged.push({ ...r });
            continue;
        }
        const last = merged[merged.length - 1];
        if (r.startSec <= last.endSec + EPS) {
            last.endSec = Math.max(last.endSec, r.endSec);
        } else {
            merged.push({ ...r });
        }
    }

    const pieces: TimeRange[] = [];
    let cursor = startSec;
    for (const m of merged) {
        if (m.startSec > cursor + EPS) {
            pieces.push({ startSec: cursor, endSec: m.startSec });
        }
        cursor = Math.max(cursor, m.endSec);
        if (cursor >= endSec - EPS) break;
    }
    if (cursor < endSec - EPS) {
        pieces.push({ startSec: cursor, endSec });
    }

    return pieces;
}
